<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :selected-division-ids="selectedDivisionIds"
            :division-options="divisionOptions"
            @update:name="awardForm.name = $event"
            @update:selectedDivisionIds="onDivisionsChange"
          >
            <template #after-divisions>
              <div class="milestone-event-type-container">
                <div class="label">
                  Milestone type
                  <small>Choose which milestone you'd like to celebrate with this award.</small>
                </div>
                <app-input
                  v-model="milestoneEventType"
                  :validate-immediate="false"
                  name="Milestone type"
                  type="select"
                  :options="milestoneOptions"
                  :undefined-allow="true"
                  undefined-label="Select type"
                  rules="required"
                />
              </div>

              <app-input
                v-if="milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY"
                v-model="anniversaryYear"
                :validate-immediate="false"
                name="Anniversary year"
                type="number"
                placeholder="e.g. 5"
                append="years"
                inputmode="numeric"
                pattern="[0-9]*"
                step="1"
                :rules="
                  milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY
                    ? 'required|min_value:1|max_value:50'
                    : ''
                "
                class="anniversary-year-input m-0"
                @input="onAnniversaryYearInput"
              >
                <template #label>
                  <div class="label">
                    Anniversary year
                    <small>Which work anniversary should trigger this award?</small>
                  </div>
                </template>
              </app-input>

              <app-input
                v-model="recipientMessage"
                :validate-immediate="false"
                name="Recipient message"
                type="textarea"
                :rows="2"
                :maxlength="500"
                rules="required|max:500"
                :description="recipientMessageDescription"
                placeholder="e.g. Congratulation on your 5 year anniversary with the company!"
                @input="onRecipientMessageInput"
              >
                <template #label>
                  <div class="label">
                    Add a message
                    <small>Write a default message that celebrates the milestone.</small>
                  </div>
                </template>
              </app-input>
            </template>
          </basic-details-section>

          <!-- Appearance -->
          <image-upload-section
            :banner-file="bannerFile"
            :banner-preview-url="bannerPreviewUrl"
            :badge-file="badgeFile"
            :badge-preview-url="badgePreviewUrl"
            :allowed-image-types="allowedImageTypes"
            :max-size-bytes="MAX_BANNER_SIZE_BYTES"
            @update:bannerFile="onBannerFileUpdate"
            @update:bannerPreviewUrl="onBannerPreviewUrlUpdate"
            @update:badgeFile="onBadgeFileUpdate"
            @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
          />

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <div class="card-container">
                <div class="label-row">
                  <div class="label">
                    Enable badges (optional)
                    <small>Turn awards into collectible badges that appear on recipients' home page.</small>
                  </div>
                  <b-form-checkbox v-model="badgesEnabled" switch />
                </div>

                <monetary-reward-section
                  :enabled="monetaryRewardEnabled"
                  :required="true"
                  :currency-symbol="currencySymbol"
                  :amounts="awardValueMinor"
                  :amount-error="awardValueError"
                  :redemption-options="selectedRedemptionOptions"
                  :redemption-error="redemptionSelectionError"
                  :expiration-months="expirationMonths"
                  @update:amounts="awardValueMinor = $event"
                  @update:redemptionOptions="selectedRedemptionOptions = $event"
                  @update:expirationMonths="expirationMonths = $event"
                >
                  <template #reward-amounts>
                    <div class="reward-amount-container">
                      <app-input
                        v-model="singleAwardValue"
                        :validate-immediate="false"
                        name="Reward amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        inputmode="decimal"
                        :prepend="currencySymbol"
                        class="amount-input"
                        @input="onAmountInput($event)"
                        @keydown="onAmountKeydown"
                        @blur="onAmountBlur()"
                        @change="onAmountBlur()"
                      />
                    </div>
                  </template>
                </monetary-reward-section>
              </div>
            </sme-card>
          </div>

          <!-- Award Status - Commented out until backend implements support
          <div>
            <h4>Award status</h4>
            <sme-card large>
              <div class="label-row">
                <div class="label">
                  Award active
                  <small
                    >When disabled, this award will not be visible to employees and no new awards will be given.</small
                  >
                </div>
                <b-form-checkbox v-model="isActive" switch />
              </div>
            </sme-card>
          </div>
          -->
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import {
  RECOGNITION_APPROVAL_TYPES,
  RECOGNITION_MILESTONE_EVENT_OPTIONS,
  RECOGNITION_MILESTONE_EVENT_TYPES,
  toTwoDecimalsOrEmpty,
} from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  monetaryRewardEnabled,
  awardValueMinor,
  awardValueError,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  bannerFile,
  bannerPreviewUrl,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBannerFileUpdate,
  onBannerPreviewUrlUpdate,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  saving,
  errorMessage,
  sanitizeAmountInput,
  majorToMinor,
  onAmountKeydown,
  loadDivisions,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  getImageFiles,
  getGrantExpiryInterval,
  validateRedemptionOptions,
} = useAwardForm();

// Milestone-specific state
const badgesEnabled = ref(true);
const milestoneEventType = ref(undefined);
const milestoneOptions = RECOGNITION_MILESTONE_EVENT_OPTIONS;
const anniversaryYear = ref('');
const recipientMessage = ref('');
const singleAwardValue = ref(undefined);
const isActive = ref(true);
const isLoading = ref(true);

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

const recipientMessageDescription = computed(() => {
  const len = String(recipientMessage.value || '').length;
  return `${len}/500 characters`;
});

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Parse ISO 8601 duration to months.
 */
const parseGrantExpiryInterval = interval => {
  if (!interval) {
    return '';
  }
  const str = String(interval);
  const monthMatch = str.match(/^P(\d+)M$/);
  if (monthMatch) {
    return monthMatch[1];
  }
  const dayMatch = str.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const months = Math.round(days / 30);
    return months > 0 ? String(months) : '';
  }
  return '';
};

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  selectedDivisionIds: [...(selectedDivisionIds.value || [])].sort(),
  milestoneEventType: milestoneEventType.value,
  anniversaryYear: anniversaryYear.value,
  recipientMessage: recipientMessage.value,
  badgesEnabled: badgesEnabled.value,
  monetaryRewardEnabled: monetaryRewardEnabled.value,
  singleAwardValue: singleAwardValue.value,
  selectedRedemptionOptions: [...(selectedRedemptionOptions.value || [])].sort(),
  expirationMonths: expirationMonths.value,
  isActive: isActive.value,
  bannerFileName: bannerFile.value?.name ?? null,
  badgeFileName: badgeFile.value?.name ?? null,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';
  isActive.value = program.is_active !== false;
  badgesEnabled.value = program.show_badge !== false;

  // Divisions
  const divisions = program.enabled_divisions || [];
  selectedDivisionIds.value = divisions.length > 0 ? divisions : [];

  // Milestone config
  const milestone = program.properties?.milestone || {};
  milestoneEventType.value = milestone.event_type || undefined;
  anniversaryYear.value = milestone.anniversary_year ? String(milestone.anniversary_year) : '';
  recipientMessage.value = milestone.recipient_message || '';

  // Images - set preview URLs from existing images
  if (program.banner_presigned_url) {
    bannerPreviewUrl.value = program.banner_presigned_url;
  } else {
    bannerPreviewUrl.value = '';
  }
  if (program.badge_presigned_url) {
    badgePreviewUrl.value = program.badge_presigned_url;
  } else {
    badgePreviewUrl.value = '';
  }
  // Clear any previously selected files
  onBannerFileUpdate(null);
  onBadgeFileUpdate(null);

  // Monetary reward - Milestone awards always require monetary rewards
  monetaryRewardEnabled.value = true;

  const valueMinor = program.properties?.award_value_minor;
  // Convert minor to major for display
  if (Number.isFinite(valueMinor)) {
    singleAwardValue.value = (valueMinor / 100).toFixed(2);
  } else {
    singleAwardValue.value = undefined;
  }

  // Redemption options
  const validGrantTypes = program.properties?.valid_grant_types || ['VOUCHER'];
  selectedRedemptionOptions.value = validGrantTypes;

  // Expiration
  expirationMonths.value = parseGrantExpiryInterval(program.grant_expiry_interval);

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  // Clear any newly selected image files
  onBannerFileUpdate(null);
  onBadgeFileUpdate(null);
  errorMessage.value = '';
  awardValueError.value = '';
  redemptionSelectionError.value = '';
};

const buildProperties = redemptionMethods => {
  // API requires at least 1 valid_grant_type - default to VOUCHER if none provided
  const grantTypes = (redemptionMethods || []).map(String);
  const props = {
    valid_grant_types: grantTypes.length > 0 ? grantTypes : ['VOUCHER'],
    approvers: undefined,
  };

  // When monetary rewards are disabled, explicitly set fields to null to clear them
  if (!monetaryRewardEnabled.value) {
    props.award_value_minor = null;
    props.default_award_value_minor = null;
  } else if (singleAwardValue.value !== undefined && singleAwardValue.value !== null) {
    const minor = majorToMinor(singleAwardValue.value);
    if (Number.isFinite(minor)) {
      props.award_value_minor = minor;
      props.default_award_value_minor = minor;
    }
  }

  if (milestoneEventType.value) {
    const eventType = String(milestoneEventType.value);
    const milestone = { event_type: eventType };
    if (eventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
      const yearInt = parseInt(String(anniversaryYear.value || '').trim(), 10);
      if (Number.isFinite(yearInt)) {
        milestone.anniversary_year = yearInt;
      }
    }
    milestone.recipient_message = String(recipientMessage.value || '').slice(0, 500);
    props.milestone = milestone;
  }

  return props;
};

const buildPayload = properties => ({
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: badgesEnabled.value,
  enabled_divisions: enabledDivisions.value,
  grant_expiry_interval: getGrantExpiryInterval(),
  is_active: isActive.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require a valid single award value when monetary rewards are enabled
    if (monetaryRewardEnabled.value) {
      const minor = majorToMinor(singleAwardValue.value);
      if (!Number.isFinite(minor) || minor <= 0) {
        awardValueError.value = 'Please enter a valid reward amount.';
        saving.value = false;
        return;
      }
      awardValueError.value = '';
    }

    const properties = buildProperties(redemptionMethods);
    const payload = buildPayload(properties);
    const files = getImageFiles();

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload, files);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);
    originalValues.value = getCurrentFormState();
    emit('updated');
  } catch (e) {
    errorMessage.value = e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  selectedDivisionIds.value = ids;
};

const onAmountInput = value => {
  singleAwardValue.value = sanitizeAmountInput(value);
};

const onAmountBlur = () => {
  singleAwardValue.value =
    singleAwardValue.value === '' || singleAwardValue.value === undefined
      ? ''
      : toTwoDecimalsOrEmpty(singleAwardValue.value);
};

const onAnniversaryYearInput = value => {
  const cleaned = String(value ?? '').replace(/[^0-9]/g, '');
  anniversaryYear.value = cleaned;
};

const onRecipientMessageInput = value => {
  recipientMessage.value = String(value ?? '').slice(0, 500);
};

watch(milestoneEventType, val => {
  if (val !== RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
    anniversaryYear.value = '';
  }
});

watch(singleAwardValue, newVal => {
  if (!monetaryRewardEnabled.value) {
    awardValueError.value = '';
    return;
  }
  const minor = majorToMinor(newVal);
  if (Number.isFinite(minor) && awardValueError.value) {
    awardValueError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    singleAwardValue.value = undefined;
  }
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadDivisions();
    setupCommonWatchers();
    populateFromProgram(props.program);
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}
</style>
